/**
 * React Hook — useExecutionStore
 *
 * Bridges vanilla TS executionStore into React.
 * SIMULATION ONLY — no real broker calls.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { executionStore } from '../store/executionStore';
import type { ExecutionState, TradePreviewRequest } from '../types/execution';

export interface UseExecutionStore {
  // State
  status: ExecutionState['status'];
  preview: ExecutionState['preview'];
  execution: ExecutionState['execution'];
  error: ExecutionState['error'];
  errorType: ExecutionState['errorType'];

  // Derived
  isIdle: boolean;
  isPreviewing: boolean;
  isExecuting: boolean;
  isSuccess: boolean;
  isError: boolean;
  hasPreview: boolean;
  hasExecution: boolean;

  // Actions
  submitPreview: (request: TradePreviewRequest) => Promise<void>;
  confirmExecution: () => Promise<void>;
  reset: () => void;
}

export function useExecutionStore(): UseExecutionStore {
  const [state, setState] = useState<ExecutionState>(executionStore.getState());
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    const unsubscribe = executionStore.subscribe((newState) => {
      if (mountedRef.current) setState(newState);
    });
    setState(executionStore.getState());
    return () => { mountedRef.current = false; unsubscribe(); };
  }, []);

  const submitPreview = useCallback(async (request: TradePreviewRequest) => {
    await executionStore.submitPreview(request);
  }, []);

  const confirmExecution = useCallback(async () => {
    await executionStore.confirmExecution();
  }, []);

  const reset = useCallback(() => {
    executionStore.reset();
  }, []);

  return {
    status: state.status,
    preview: state.preview,
    execution: state.execution,
    error: state.error,
    errorType: state.errorType,

    isIdle: state.status === 'idle',
    isPreviewing: state.status === 'previewing',
    isExecuting: state.status === 'executing',
    isSuccess: state.status === 'success',
    isError: state.status === 'error',
    hasPreview: state.preview !== null,
    hasExecution: state.execution !== null,

    submitPreview,
    confirmExecution,
    reset,
  };
}
